import { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Label,
  Radio,
  RadioGroup,
  Text,
  type Omittable,
} from "@salestrak/ui";
import {
  isOutcomeTone,
  OUTCOME_TONE_CONFIG,
  OUTCOME_TONES,
  type OutcomeTone,
} from "#app/lib/outcome-tone";
import { OutcomeToneDot } from "./outcome-tone-dot";

type OutcomeFormDialogProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Rejects to keep the dialog open — the caller has already said why. */
  onSubmit: (values: { name: string; tone: OutcomeTone }) => Promise<void>;
  /** Present in edit mode; absent means create. */
  initialName?: string;
  /** Seeded by the tone section whose `+` opened a create dialog. */
  initialTone?: OutcomeTone;
  /** What the create mode says the outcome is FOR — the one line that differs
   *  between the Project pool and the Account/Contact pools. */
  createSubtitle?: string;
}>;

const DEFAULT_TONE: OutcomeTone = "neutral";

/**
 * Create or edit one outcome — a name and a tone.
 *
 * Serves both pools: the server `Reason` vocabulary Projects post against, and
 * the local Account/Contact pools. "Reason" is only the stored name, so the
 * copy here says outcome throughout.
 */
export function OutcomeFormDialog({
  open,
  onOpenChange,
  onSubmit,
  initialName,
  initialTone,
  createSubtitle,
}: OutcomeFormDialogProps) {
  const isEdit = initialName != null;
  const [name, setName] = useState(initialName ?? "");
  const [tone, setTone] = useState<OutcomeTone>(initialTone ?? DEFAULT_TONE);
  const [submitting, setSubmitting] = useState(false);
  const [nameError, setNameError] = useState("");

  useEffect(() => {
    if (!open) return;
    setName(initialName ?? "");
    setTone(initialTone ?? DEFAULT_TONE);
    setNameError("");
  }, [open, initialName, initialTone]);

  async function handleSubmit() {
    if (submitting) return;
    // A blank name says so, rather than a press that does nothing.
    if (!name.trim()) {
      setNameError("Name is required");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({ name: name.trim(), tone });
      onOpenChange(false);
    } catch {
      // Refused (a taken name): the caller toasted the reason, and the typed
      // name stays for the person to fix.
    } finally {
      setSubmitting(false);
    }
  }

  const title = isEdit ? "Edit outcome" : "Create outcome";
  const subtitle = isEdit
    ? "Name and tone."
    : createSubtitle;
  const primaryLabel = isEdit ? "Save" : "Create";

  // One Dialog serves both breakpoints, and Enter submits the form.
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup
        dialogOptions={{
          primary: { label: primaryLabel, onClick: handleSubmit, loading: submitting },
          cancel: { disabled: submitting },
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {subtitle ? <DialogDescription>{subtitle}</DialogDescription> : null}
        </DialogHeader>
        <DialogPanel>
          <Box
            as="form"
            gap="md"
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit();
            }}
          >
            <Field invalid={!!nameError}>
              <FieldLabel>Name</FieldLabel>
              <Input
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setNameError("");
                }}
                placeholder="Outcome name"
                autoFocus
              />
              {nameError ? <FieldError match>{nameError}</FieldError> : null}
            </Field>
            {/* Not a Field: a FieldLabel names every control in its Field, so
              * each radio read as "Tone". The group carries "Tone", and the
              * Label wrapping each control makes its text both the radio's
              * accessible name and the hit target. */}
            <Box gap="xs">
              <Text size="xs" weight="medium" variant="muted">
                Tone
              </Text>
              <RadioGroup
                aria-label="Tone"
                value={tone}
                onValueChange={(next) => {
                  if (isOutcomeTone(next)) setTone(next);
                }}
              >
                {OUTCOME_TONES.map((option) => (
                  <Label key={option}>
                    <Radio value={option} />
                    <OutcomeToneDot tone={option} />
                    <Text size="sm" weight="normal">
                      {OUTCOME_TONE_CONFIG[option].label}
                    </Text>
                  </Label>
                ))}
              </RadioGroup>
            </Box>
          </Box>
        </DialogPanel>
      </DialogPopup>
    </Dialog>
  );
}
